/**
 * Stable "sign in / no access" screen for standalone agent pages.
 *
 * Shown when an anonymous visitor (or a signed-in user without access) lands on
 * a published agent's public page. Replaces the previous behaviour where an
 * anonymous visitor was bounced through SessionProvider → /signin and, on a
 * custom domain, ping-ponged with the catch-all redirect — producing a
 * flickering reload loop. This screen never auto-navigates, so the loop can't
 * form; the user signs in only when they click the button.
 *
 * SSO-aware: on an agent custom domain whose workspace has SSO enabled, it
 * offers "Sign in with SSO", which starts the workspace SSO flow and returns
 * the visitor to this agent origin (see server `/auth/sso/*` handoff).
 */

import { useEffect, useState } from 'react'
import { Lock } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { publicApi } from '@/lib/api/public'
import { API_URL } from '@/lib/env'
import { isKnownAppDomain } from '@/hooks/use-domain-routing'

interface AgentAccessRequiredProps {
  /**
   * 'signin' — anonymous visitor who just needs to authenticate.
   * 'no-access' — authenticated user who lacks access to (or can't find) the agent.
   */
  mode?: 'signin' | 'no-access'
}

export function AgentAccessRequired({ mode = 'signin' }: AgentAccessRequiredProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const isNoAccess = mode === 'no-access'

  // SSO context for the current agent custom domain (resolved from the public
  // domain lookup). Only populated when the owning workspace has SSO enabled.
  const [sso, setSso] = useState<{ enabled: boolean; slug: string; required: boolean } | null>(null)

  useEffect(() => {
    if (isNoAccess) return
    const host = window.location.hostname
    // SSO sign-in only applies to agent custom domains, not the main app hosts.
    if (isKnownAppDomain(host)) return

    let cancelled = false
    publicApi
      .lookupDomain(host)
      .then((r) => {
        if (cancelled) return
        if (r.found && r.ssoEnabled && r.workspaceSlug) {
          setSso({
            enabled: true,
            slug: r.workspaceSlug,
            required: r.ssoEnforcement === 'required',
          })
        }
      })
      .catch(() => {
        /* fall back to standard sign-in */
      })
    return () => {
      cancelled = true
    }
  }, [isNoAccess])

  const handleSignIn = () => {
    const redirectUrl = encodeURIComponent(location.pathname + location.search)
    navigate(`/signin?redirectUrl=${redirectUrl}`, { replace: true })
  }

  const handleSsoSignIn = () => {
    if (!sso) return
    // Return to the exact agent URL on this origin after SSO completes.
    const returnTo = `${window.location.origin}${location.pathname}${location.search}`
    const url =
      `${API_URL}/auth/sso/authorize?slug=${encodeURIComponent(sso.slug)}` +
      `&redirect_uri=${encodeURIComponent(returnTo)}`
    window.location.href = url
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Lock className="h-6 w-6 text-primary" />
        </div>
        <h1 className="text-xl font-semibold mb-2">
          {isNoAccess ? "You don't have access" : 'Sign in to continue'}
        </h1>
        <p className="text-muted-foreground mb-6">
          {isNoAccess
            ? "This agent doesn't exist or you don't have permission to access it. Sign in with an account that has access."
            : 'You need to sign in to chat with this agent.'}
        </p>

        <div className="flex flex-col items-center gap-2">
          {sso?.enabled && !isNoAccess ? (
            <>
              <Button onClick={handleSsoSignIn} className="w-full sm:w-auto">
                Sign in with SSO
              </Button>
              {!sso.required && (
                <Button
                  variant="ghost"
                  onClick={handleSignIn}
                  className="w-full sm:w-auto"
                >
                  Use email instead
                </Button>
              )}
            </>
          ) : (
            <Button onClick={handleSignIn} className="w-full sm:w-auto">
              {isNoAccess ? 'Sign in with another account' : 'Sign in'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

AgentAccessRequired.displayName = 'AgentAccessRequired'
